import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  collection, collectionGroup, doc, onSnapshot, query, orderBy, runTransaction, serverTimestamp,
  getDocs,
} from 'firebase/firestore';
import type { DocumentSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import AppLayout from '../components/AppLayout';
import { useItemParam } from '../hooks/useNewParam';
import { usePaging } from '../hooks/usePaging';
import { Paging } from '../components/ui/Paging';
import { Button, FormField, CalcField, Modal } from '../components/ui';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import type { InventoryItem, InventoryStatus, ItemCode, Sku, CodeHistoryEntry } from '../types';

function todayISO() { return new Date().toISOString().split('T')[0]; }
function num(s: string) { return parseFloat(s) || 0; }
function round2(n: number) { return Math.round(n * 100) / 100; }
// A 'split-out' CodeHistoryEntry's `notes` field is written as "To code {code}"
// (see handleComplete) — pull just the code back out for the History table's To column.
function parseDestCode(notes?: string): string | undefined {
  return notes?.startsWith('To code ') ? notes.slice('To code '.length) : undefined;
}
// carat - whatever's held by concurrent memos — see InventoryMemo/memoCommittedCarat in
// types/index.ts. Same helper used by Sales.tsx/Inventory.tsx; a Split cut is capped at
// this, not the item's full carat, so material held on memo elsewhere stays untouchable.
function availableCarat(item: InventoryItem): number {
  return round2(Math.max(0, item.carat - (item.memoCommittedCarat ?? 0)));
}
function slugifySku(sku: string): string {
  return sku.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
function stripUndef<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(v => stripUndef(v)) as unknown as T;
  if (obj !== null && typeof obj === 'object' && (obj as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndef(v)])
    ) as T;
  }
  return obj;
}

function IconSearch() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconEmptyDiamond() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <polygon points="4,2 12,2 14.5,6 8,14.5 1.5,6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 14.5V6M1.5 6h13M4 2l4 4M12 2l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconX() {
  return <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>;
}
function IconArrowRight() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconInfo() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M7 6.3v3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <circle cx="7" cy="4.3" r="0.9" fill="currentColor" />
  </svg>;
}

function CodePill({ code }: { code?: string }) {
  if (!code) return <span style={{ fontSize: 12, color: 'var(--text-2)', opacity: 0.4 }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
    }}>
      {code}
    </span>
  );
}

// Label-over-value pair for the destination-match banner in the Add-Cut form.
function MatchFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{value}</div>
    </div>
  );
}

// ── DestCodeCombobox — free-text Code input with a Code/SKU/Size suggestion
// panel, so two similarly-numbered parcels don't look identical while
// picking a destination. Unlike EntityCombobox, the typed text itself IS the
// value (a brand-new Code that matches nothing is a normal, expected
// outcome) — clicking a suggestion just fills the text in, it doesn't lock
// the field to only-existing-values the way a true id-backed combobox would.
function DestCodeCombobox({
  items, value, onChange, excludeCode, invalid,
}: {
  items: InventoryItem[]; value: string; onChange: (v: string) => void; excludeCode?: string; invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 280) });
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const q = value.trim().toLowerCase();
  const filtered = items
    .filter(i => i.code && i.code !== excludeCode)
    .filter(i => !q || (i.code ?? '').toLowerCase().includes(q) || (i.sku ?? '').toLowerCase().includes(q))
    .slice(0, 30);

  function select(code: string) {
    onChange(code);
    setOpen(false);
    inputRef.current?.blur();
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); } }}
        placeholder="Type or pick a Code"
        autoComplete="off"
        style={{ fontFamily: 'var(--font-num)', ...(invalid ? { borderColor: 'var(--danger)' } : undefined) }}
      />
      {open && pos && createPortal(
        <div
          ref={panelRef}
          className="dropdown-panel"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 280, overflowY: 'auto' }}
        >
          <div className="dropdown-group-label">Code · SKU · Size</div>
          {filtered.length === 0 && (
            <div style={{ padding: '8px 9px', fontSize: 12, color: 'var(--text-3)' }}>
              No existing matches — this will be a new Code.
            </div>
          )}
          {filtered.map(i => (
            <button key={i.id} type="button" className="dropdown-item" onClick={() => select(i.code!)}>
              <CodePill code={i.code} />
              <span style={{ fontFamily: 'var(--font-num)', fontSize: 11.5, color: 'var(--text-2)' }}>{i.sku || '—'}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-3)' }}>
                {i.size || i.subSize ? `${i.size || '—'} / ${i.subSize || '—'}` : i.shape}
              </span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

interface PendingCut {
  _key: string;
  code: string;
  carat: string;
  pricePerCt: string;
  newSku: string; // only used/shown when `code` doesn't match an existing item
}
let _cutKey = 0;
// Slack allowed when checking a cut's value against what's left on the
// source parcel — ordinary carat/price rounding noise (e.g. 9.80 vs 9.90),
// not a loophole for genuinely overpricing a cut.
const VALUE_TOLERANCE_USD = 0.10;

export default function InventoryMerge() {
  const { business, user } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [allLoose, setAllLoose] = useState<InventoryItem[]>([]);
  const [loading,  setLoading]  = useState(true);

  // ── From: source-parcel picker ──────────────────────────────────────────
  const [fromItemId, setFromItemId] = useState<string | null>(null);
  const [search,      setSearch]      = useState('');
  const [shapeFilter, setShapeFilter] = useState('all');

  // ── To: pending cuts (nothing here touches Firestore until Complete) ────
  const [cuts, setCuts] = useState<PendingCut[]>([]);
  const [codeInput,  setCodeInput]  = useState('');
  const [caratInput, setCaratInput] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [newSkuInput, setNewSkuInput] = useState('');
  const [cutError, setCutError] = useState('');

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [saved,  setSaved]  = useState(false);

  // Every in-stock loose item — used both as the From-eligibility pool (must
  // also have a SKU and some available carat) and as the lookup table for
  // "does this typed destination Code already exist?" (any in-stock loose
  // item is a valid blend target, regardless of its own memo/SKU state).
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'inventory'), orderBy('createdAt', 'desc')),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem));
        setAllLoose(all.filter(i => i.type === 'polished-loose' && i.status === 'in-stock'));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  // Split & Merge history lives in a modal (opened from the header), not on
  // the page — with thousands of splits over time it doesn't belong inline.
  // Loaded on demand (one-shot getDocs, not a live listener) only while the
  // modal is open: a collectionGroup read across every
  // itemCodes/{codeId}/history subcollection, filtered client-side to
  // 'split-out' entries (one per completed cut — its `notes` field already
  // records the destination Code, so one row per cut tells the whole
  // story). No where()/orderBy() on the query, same reasoning as
  // Memos.tsx/Dashboard.tsx: avoids needing a composite index for a
  // collectionGroup query.
  const HISTORY_CAP = 150;
  const [historyOpen,    setHistoryOpen]    = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyQ,       setHistoryQ]       = useState('');
  const [splitHistory, setSplitHistory] = useState<Array<{ id: string; codeId: string; data: CodeHistoryEntry }>>([]);
  useEffect(() => {
    if (!historyOpen) return;
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    getDocs(collectionGroup(db, 'history'))
      .then(snap => {
        if (cancelled) return;
        const rows = snap.docs
          .map(d => ({ id: d.id, codeId: d.ref.parent.parent?.id ?? '', data: d.data() as CodeHistoryEntry }))
          .filter(r => r.data.type === 'split-out')
          .sort((a, b) => (b.data.date ?? '').localeCompare(a.data.date ?? '') || b.id.localeCompare(a.id))
          .slice(0, HISTORY_CAP);
        setSplitHistory(rows);
        setHistoryLoading(false);
      })
      .catch(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [historyOpen]);

  const historyRows = useMemo(() => {
    const q = historyQ.trim().toLowerCase();
    if (!q) return splitHistory;
    return splitHistory.filter(r =>
      r.codeId.toLowerCase().includes(q) ||
      (parseDestCode(r.data.notes) ?? '').toLowerCase().includes(q));
  }, [splitHistory, historyQ]);
  const histPg = usePaging(historyRows, historyQ);

  const codeIndex = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    for (const i of allLoose) if (i.code) m.set(normalizeCode(i.code), i);
    return m;
  }, [allLoose]);

  // From eligibility: a source with a SKU and some available carat. Unlike
  // the old whole-parcel Merge (which required ZERO carat on memo — it
  // folded the whole item away), Split shrinks the source in place and
  // leaves it in-stock, so partial memo availability is fine — each cut is
  // simply capped at `availableCarat`, same as everywhere else in the app.
  const fromEligible = useMemo(
    () => allLoose.filter(i => !!i.sku && availableCarat(i) > 1e-9),
    [allLoose],
  );

  const shapes = useMemo(() => [...new Set(fromEligible.map(i => i.shape))].sort(), [fromEligible]);

  const visiblePicker = useMemo(() => {
    let list = fromEligible;
    if (shapeFilter !== 'all') list = list.filter(i => i.shape === shapeFilter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(i =>
      (i.code ?? '').toLowerCase().includes(q) ||
      (i.sku ?? '').toLowerCase().includes(q) ||
      i.shape.toLowerCase().includes(q) ||
      i.color.toLowerCase().includes(q) ||
      i.clarity.toLowerCase().includes(q)
    );
    return list;
  }, [fromEligible, shapeFilter, search]);

  const fromItem = useMemo(() => allLoose.find(i => i.id === fromItemId) ?? null, [allLoose, fromItemId]);
  const fromStartPricePerCt = fromItem && fromItem.carat > 0 ? fromItem.costingUSD / fromItem.carat : 0;

  // ── Live running math (pure client state — nothing committed yet) ───────
  const cutsTotalCarat = cuts.reduce((s, c) => s + num(c.carat), 0);
  const cutsTotalValue = cuts.reduce((s, c) => s + num(c.carat) * num(c.pricePerCt), 0);
  const remainingCarat = fromItem ? Math.max(0, fromItem.carat - cutsTotalCarat) : 0;
  const remainingValue = fromItem ? Math.max(0, fromItem.costingUSD - cutsTotalValue) : 0;
  const remainingPricePerCt = remainingCarat > 1e-9 ? remainingValue / remainingCarat : 0;

  // Per-cut "what's left in the source after this specific cut" — a running
  // subtraction in cut order, mirroring the spreadsheet's "After Nth Cut" rows.
  const cutsWithRunning = useMemo(() => {
    if (!fromItem) return [];
    let carat = fromItem.carat, value = fromItem.costingUSD;
    return cuts.map(c => {
      const cutCarat = num(c.carat), cutTotal = cutCarat * num(c.pricePerCt);
      carat = Math.max(0, carat - cutCarat);
      value = Math.max(0, value - cutTotal);
      const existing = codeIndex.get(normalizeCode(c.code));
      return { cut: c, total: cutTotal, remainingCaratAfter: carat, remainingValueAfter: value, existing };
    });
  }, [cuts, fromItem, codeIndex]);

  function selectFrom(id: string) {
    // Auto-fill Price/ct with this parcel's own starting price/ct — a
    // starting value, not a lock; freely editable like a manually-typed
    // one. Looked up directly from `allLoose` rather than the `fromItem`
    // memo, which still reflects the PREVIOUS selection at this point in
    // the same call (state updates below haven't re-rendered yet).
    const item = allLoose.find(i => i.id === id);
    const startPricePerCt = item && item.carat > 0 ? item.costingUSD / item.carat : 0;
    setFromItemId(id);
    setCuts([]);
    setCodeInput(''); setCaratInput('');
    setPriceInput(startPricePerCt > 0 ? startPricePerCt.toFixed(2) : '');
    setNewSkuInput('');
    setCutError(''); setError('');
  }
  // Opened from the Inventory popup (?item=…): start with that parcel as From,
  // once the stock list has loaded.
  useItemParam(selectFrom, !loading);
  function changeFrom() {
    setFromItemId(null);
    setCuts([]);
  }

  const matchedDest = codeInput.trim() ? codeIndex.get(normalizeCode(codeInput)) : undefined;
  const isNewDestCode = !!codeInput.trim() && !matchedDest;
  // Carat can never exceed what's actually left on the source parcel — flagged live
  // (red border + note) as the user types, on top of the hard block in validateCutInputs.
  const caratExceedsRemaining = num(caratInput) > remainingCarat + 1e-6;
  // Same idea for value — carat can be within bounds while the typed Price/ct
  // still makes this cut worth more than the parcel has left (see
  // validateCutInputs). Flagged live the same way, on the Price/ct field.
  // A dime of slack (not a cent) — ordinary rounding noise from carat/price
  // multiplication (e.g. 9.80 vs 9.90) shouldn't trip this; a real overage
  // is always much bigger than that in practice.
  const cutValueExceedsRemaining = num(caratInput) * num(priceInput) > remainingValue + VALUE_TOLERANCE_USD;
  // A cut can't target the source parcel's own Code — same live-warning treatment.
  const isSourceOwnCode = !!fromItem?.code && !!codeInput.trim() && normalizeCode(codeInput) === normalizeCode(fromItem.code);

  function validateCutInputs(carat: string, price: string): string {
    if (!fromItem) return 'Select a source parcel first.';
    if (!codeInput.trim()) return 'Enter a destination Code.';
    if (fromItem.code && normalizeCode(codeInput) === normalizeCode(fromItem.code)) {
      return "A cut can't target the source parcel's own Code.";
    }
    if (cuts.some(c => normalizeCode(c.code) === normalizeCode(codeInput))) {
      return 'That Code is already used by another pending cut — remove it first, or adjust that cut instead.';
    }
    const c = num(carat);
    if (!c || c <= 0) return 'Enter a carat amount for this cut.';
    if (c > remainingCarat + 1e-6) return `Only ${remainingCarat.toFixed(2)}ct remains on the source parcel.`;
    const p = num(price);
    if (!p || p <= 0) return 'Enter a Price/ct for this cut.';
    // Carat alone isn't enough — a cut can stay within the remaining carat
    // and still be priced so its own value (carat × Price/ct) is worth more
    // than the parcel actually has left. Same idea as not being able to
    // hand someone $110 out of a $100 pocket: the source parcel's value is
    // a fixed pool, not something a cut's price can inflate past.
    const cutValue = c * p;
    if (cutValue > remainingValue + VALUE_TOLERANCE_USD) {
      return `This cut would be worth ${money(cutValue)}, but only ${money(remainingValue)} of value remains on the source parcel — lower the Price/ct or carat.`;
    }
    if (isNewDestCode && !newSkuInput.trim() && !fromItem.sku) return 'Enter a SKU for the new destination item.';
    return '';
  }

  function commitCut(carat: string, price: string) {
    const err = validateCutInputs(carat, price);
    if (err) { setCutError(err); return; }
    setCuts(prev => [...prev, {
      _key: String(++_cutKey), code: codeInput.trim(), carat, pricePerCt: price,
      newSku: newSkuInput.trim(),
    }]);
    // Auto-fill the next cut's Price/ct with the source's own price/ct
    // AFTER this cut — same number the placeholder would show once the
    // state below settles, just pre-typed instead of left blank. Computed
    // here (not read back from remainingCarat/remainingPricePerCt) since
    // those derive from `cuts` state, which hasn't re-rendered yet at this
    // point in the same call. Still just a starting value — freely
    // editable/overridable, same as a manually-typed Price/ct.
    const nextCarat = Math.max(0, remainingCarat - num(carat));
    const nextValue = Math.max(0, remainingValue - num(carat) * num(price));
    const nextPricePerCt = nextCarat > 1e-9 ? nextValue / nextCarat : 0;
    setCodeInput(''); setCaratInput('');
    setPriceInput(nextPricePerCt > 0 ? nextPricePerCt.toFixed(2) : '');
    setNewSkuInput(''); setCutError('');
  }
  function addCut() { commitCut(caratInput, priceInput); }
  function removeCut(key: string) {
    setCuts(prev => prev.filter(c => c._key !== key));
  }

  function validateComplete(): string {
    if (!fromItem) return 'Select a source parcel first.';
    if (cuts.length === 0) return 'Add at least one cut before completing.';
    const codes = cuts.map(c => normalizeCode(c.code));
    const dupe = codes.find((c, i) => codes.indexOf(c) !== i);
    if (dupe) return 'Two cuts use the same destination Code — remove one before completing.';
    if (cutsTotalCarat > availableCarat(fromItem) + 1e-6) {
      return "Total carat allocated exceeds the source parcel's available carat.";
    }
    // Final safety net mirroring the per-cut check in validateCutInputs —
    // catches it too if cuts were ever added out of order in a way that
    // let the combined value creep past what the parcel is actually worth.
    if (cutsTotalValue > fromItem.costingUSD + VALUE_TOLERANCE_USD) {
      return `Total value allocated (${money(cutsTotalValue)}) exceeds the source parcel's own value (${money(fromItem.costingUSD)}).`;
    }
    return '';
  }

  async function handleComplete() {
    const err = validateComplete();
    if (err) { setError(err); return; }
    setSaving(true); setError(''); setSaved(false);
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }
    const from = fromItem!;
    const fromRef = doc(db, 'inventory', from.id);
    const now = todayISO();

    const cutRefs = cuts.map(c => ({
      cut: c,
      codeId: normalizeCode(c.code),
      codeRef: doc(db, 'itemCodes', normalizeCode(c.code)),
    }));

    try {
      await runTransaction(db, async tx => {
        // ── Reads (all before any writes) ──
        const freshFromSnap = await tx.get(fromRef);
        if (!freshFromSnap.exists()) throw new Error('Source parcel no longer exists.');
        const freshFrom = freshFromSnap.data() as InventoryItem;
        if (freshFrom.status !== 'in-stock') throw new Error('Source parcel is no longer in stock — reload and try again.');
        const freshAvail = freshFrom.carat - (freshFrom.memoCommittedCarat ?? 0);
        const totalCutCarat = cuts.reduce((s, c) => s + num(c.carat), 0);
        if (totalCutCarat > freshAvail + 1e-6) {
          throw new Error('The source parcel no longer has enough available carat — reload and try again.');
        }

        const codeSnaps = [];
        for (const cr of cutRefs) codeSnaps.push(await tx.get(cr.codeRef));

        const destItemSnaps: (DocumentSnapshot | null)[] = [];
        for (let i = 0; i < cutRefs.length; i++) {
          const cs = codeSnaps[i];
          destItemSnaps.push(cs.exists() ? await tx.get(doc(db, 'inventory', (cs.data() as ItemCode).inventoryItemId)) : null);
        }
        destItemSnaps.forEach((s, i) => {
          if (s && s.exists()) {
            const d = s.data() as InventoryItem;
            if (d.type !== 'polished-loose' || d.status !== 'in-stock') {
              throw new Error(`Destination Code "${cutRefs[i].cut.code}" isn't an available loose in-stock item anymore.`);
            }
          }
        });

        const skuInfos = cutRefs.map((cr, i) => {
          const destSnap = destItemSnaps[i];
          if (destSnap && destSnap.exists()) {
            const d = destSnap.data() as InventoryItem;
            return d.skuId ? { skuId: d.skuId, sku: d.sku } : null;
          }
          const newSku = (cr.cut.newSku.trim() || freshFrom.sku || '').trim();
          return newSku ? { skuId: slugifySku(newSku), sku: newSku } : null;
        });
        const skuRefs = skuInfos.map(info => info ? doc(db, 'skus', info.skuId) : null);
        const skuSnaps: (DocumentSnapshot | null)[] = [];
        for (const ref of skuRefs) skuSnaps.push(ref ? await tx.get(ref) : null);

        // ── Writes ──
        let remCarat = freshFrom.carat, remCostUSD = freshFrom.costUSD, remCostingUSD = freshFrom.costingUSD;

        cutRefs.forEach((cr, i) => {
          const cutCarat = num(cr.cut.carat);
          const cutPrice = num(cr.cut.pricePerCt);
          const cutTotal = cutCarat * cutPrice;
          remCarat -= cutCarat; remCostUSD -= cutTotal; remCostingUSD -= cutTotal;

          const destSnap = destItemSnaps[i];
          const skuRef = skuRefs[i], skuSnap = skuSnaps[i], skuInfo = skuInfos[i];
          let destItemId: string, destCaratFinal: number, destCostingFinal: number;

          if (destSnap && destSnap.exists()) {
            const d = destSnap.data() as InventoryItem;
            destItemId = destSnap.id;
            destCaratFinal = d.carat + cutCarat;
            destCostingFinal = d.costingUSD + cutTotal;
            tx.update(doc(db, 'inventory', destItemId), stripUndef({
              carat: destCaratFinal, costUSD: d.costUSD + cutTotal, costingUSD: destCostingFinal,
              mergedFromIds: [...(d.mergedFromIds ?? []), from.id],
              updatedAt: serverTimestamp(),
            }));
          } else {
            const newRef = doc(collection(db, 'inventory'));
            destItemId = newRef.id; destCaratFinal = cutCarat; destCostingFinal = cutTotal;
            tx.set(newRef, stripUndef({
              businessId: business!.id, purchaseId: '', purchaseNo: '', lineItemId: '',
              type: 'polished-loose' as const, status: 'in-stock' as InventoryStatus,
              shape: freshFrom.shape, color: freshFrom.color, clarity: freshFrom.clarity,
              size: freshFrom.size, subSize: freshFrom.subSize,
              sku: skuInfo?.sku, skuId: skuInfo?.skuId,
              code: cr.cut.code.trim(), codeId: cr.codeId,
              carat: cutCarat, costUSD: cutTotal, costingUSD: cutTotal,
              splitFromId: from.id,
              createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
            }));
            tx.set(cr.codeRef, stripUndef({
              businessId: business!.id, code: cr.cut.code.trim(), inventoryItemId: newRef.id,
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }

          tx.set(doc(collection(db, 'itemCodes', cr.codeId, 'history')), stripUndef({
            type: 'split-in' as const, date: now, carat: cutCarat, pricePerCt: cutPrice, amountUSD: cutTotal,
            inventoryItemId: destItemId, notes: freshFrom.code ? `From code ${freshFrom.code}` : undefined,
            createdAt: serverTimestamp(), createdBy: user!.id,
          }));
          if (freshFrom.code) {
            tx.set(doc(collection(db, 'itemCodes', normalizeCode(freshFrom.code), 'history')), stripUndef({
              type: 'split-out' as const, date: now, carat: cutCarat, pricePerCt: cutPrice, amountUSD: cutTotal,
              inventoryItemId: from.id, notes: `To code ${cr.cut.code.trim()}`,
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }

          if (skuRef && skuInfo) {
            const destCostPerCt = destCaratFinal > 0 ? destCostingFinal / destCaratFinal : 0;
            const existed = !!skuSnap?.exists();
            const prior = existed ? (skuSnap!.data() as Sku) : null;
            tx.set(skuRef, stripUndef({
              businessId: business!.id, sku: skuInfo.sku ?? prior?.sku,
              shape: freshFrom.shape, color: freshFrom.color, clarity: freshFrom.clarity,
              size: freshFrom.size, subSize: freshFrom.subSize,
              currentAvgCostPerCt: destCostPerCt, currentAddedValuePercent: 0, currentCostingPerCt: destCostPerCt,
              lastActivityAt: now,
              createdAt: existed ? prior!.createdAt : serverTimestamp(),
              updatedAt: serverTimestamp(),
            }));
            tx.set(doc(collection(db, 'skus', skuInfo.skuId, 'history')), stripUndef({
              type: 'merge' as const, date: now,
              mergedInventoryItemIds: [from.id], mergedQuantityCarat: cutCarat,
              addedValuePercentApplied: 0, newInventoryItemId: destItemId,
              skuPreExisted: existed, previousAddedValuePercent: existed ? 0 : undefined,
              previousAvgCostPerCt: prior?.currentAvgCostPerCt ?? 0, newAvgCostPerCt: destCostPerCt,
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }
        });

        const fromFullyConsumed = remCarat <= 1e-6;
        tx.update(fromRef, stripUndef({
          carat: fromFullyConsumed ? 0 : remCarat,
          costUSD: fromFullyConsumed ? 0 : remCostUSD,
          costingUSD: fromFullyConsumed ? 0 : remCostingUSD,
          status: fromFullyConsumed ? ('merged' as InventoryStatus) : freshFrom.status,
          updatedAt: serverTimestamp(),
        }));

        logActivity(tx, db, business!.id, user, {
          module: 'split-merge', action: 'split', recordId: from.id, recordNo: from.code || from.sku || undefined,
          itemCount: cuts.length, carat: cutsTotalCarat, amountUSD: cutsTotalValue,
          note: `To ${cuts.map(c => c.code.trim()).join(', ')}`,
        });
      });

      setFromItemId(null); setCuts([]);
      setCodeInput(''); setCaratInput(''); setPriceInput(''); setNewSkuInput('');
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to complete split & merge.');
    }
    setSaving(false);
  }

  return (
    <AppLayout
      title="Split & Merge"
      subtitle="Cut carat off one source parcel and allocate each piece to a destination Code — nothing is written until you complete it"
      headerActions={
        <Button
          variant="secondary" size="sm"
          onClick={() => { setHistoryQ(''); setSplitHistory([]); setHistoryLoading(true); setHistoryOpen(true); }}
        >
          History
        </Button>
      }
    >
      {saved && (
        <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>
          ✓ Split & Merge completed — the source parcel and destination Code(s) are updated.
        </div>
      )}
      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* ── From ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', borderBottom: fromItem ? 'none' : '1px solid var(--border)' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>From — source parcel</div>
        </div>

        {!fromItem ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 220, flexShrink: 0, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' }}>
                  <IconSearch />
                </span>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Code, SKU, shape, color..."
                  style={{
                    width: '100%', paddingLeft: 32, paddingRight: 10,
                    height: 32, fontSize: 12.5, border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r-sm)', background: 'var(--surface-2)',
                    color: 'var(--text-1)', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <select className="toolbar-select" value={shapeFilter} onChange={e => setShapeFilter(e.target.value)} style={{ width: 140 }}>
                <option value="all">All Shapes</option>
                {shapes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>{visiblePicker.length} eligible</span>
            </div>

            {loading ? (
              <div style={{ padding: 24, fontSize: 13, color: 'var(--text-3)' }}>Loading…</div>
            ) : visiblePicker.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px 20px' }}>
                <div className="empty-state-icon" style={{ display: 'flex', justifyContent: 'center' }}><IconEmptyDiamond /></div>
                <div className="empty-state-title">No eligible loose parcels</div>
                <div className="empty-state-sub">A source needs a SKU and some available (not fully memo'd) carat.</div>
              </div>
            ) : (
              <div className="table-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Code</th><th>SKU</th><th>Shape</th><th>Color</th><th>Clarity</th>
                      <th>Carat</th><th>Available</th><th>Price/ct</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePicker.map(item => (
                      <tr key={item.id} onClick={() => selectFrom(item.id)} style={{ cursor: 'pointer' }}>
                        <td><CodePill code={item.code} /></td>
                        <td><CodePill code={item.sku} /></td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{item.shape}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{item.color}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{item.clarity}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{item.carat.toFixed(2)}</td>
                        <td style={{ fontSize: 12, color: availableCarat(item) < item.carat - 1e-9 ? 'var(--warning)' : 'var(--text-2)', fontFamily: 'var(--font-num)', fontWeight: availableCarat(item) < item.carat - 1e-9 ? 700 : 400 }}>
                          {availableCarat(item).toFixed(2)}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                          {money(item.carat > 0 ? item.costingUSD / item.carat : 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '4px 16px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {[
                { label: 'Code',   val: fromItem.code || '—' },
                { label: 'SKU',    val: fromItem.sku || '—' },
                { label: 'Shape',  val: fromItem.shape },
                { label: 'Color/Clarity', val: `${fromItem.color}/${fromItem.clarity}` },
                { label: 'Start Carat',   val: `${fromItem.carat.toFixed(2)} ct` },
                { label: 'Start Price/ct', val: money(fromStartPricePerCt) },
              ].map(({ label, val }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)', padding: '4px 10px', fontSize: 11.5,
                }}>
                  <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>{label}</span>
                  <span style={{ width: 1, height: 11, background: 'var(--border)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-1)', fontWeight: 700, fontFamily: 'var(--font-num)' }}>{val}</span>
                </div>
              ))}
              <Button variant="secondary" size="sm" onClick={changeFrom} style={{ marginLeft: 'auto' }}>Change Source</Button>
            </div>

            {/* Live "Remaining" readout — this is the number that shrinks with every cut, exactly the spreadsheet's "After Nth Cut" row */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              background: 'var(--primary-light)', borderRadius: 'var(--r-sm)', marginBottom: cuts.length ? 12 : 0,
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Remaining</span>
              <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 13 }}>{remainingCarat.toFixed(2)} ct</span>
              <span style={{ color: 'var(--text-3)' }}>·</span>
              <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 13 }}>{money(remainingPricePerCt)}/ct</span>
              <span style={{ color: 'var(--text-3)' }}>·</span>
              <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 13 }}>{money(remainingValue)} total</span>
            </div>

            {cuts.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {cutsWithRunning.map(({ cut, total, remainingCaratAfter, remainingValueAfter }, idx) => (
                  <div key={cut._key} style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '3px 0' }}>
                    Cut {idx + 1}: <strong style={{ color: 'var(--text-1)' }}>{num(cut.carat).toFixed(2)}ct</strong> @ {money(num(cut.pricePerCt))}/ct
                    (<span style={{ fontFamily: 'var(--font-num)' }}>{money(total)}</span>) → <CodePill code={cut.code} /> — source left:{' '}
                    <span style={{ fontFamily: 'var(--font-num)' }}>{remainingCaratAfter.toFixed(2)}ct / {money(remainingValueAfter)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── To ── */}
      {fromItem && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            To — where does this cut go?
            <span
              title="The Destination Code decides it: type an existing Code and this cut blends into that parcel; type a brand-new Code (one that doesn't exist yet) and a fresh parcel is created from this cut — that's how you split a parcel out into a separate new one."
              style={{ display: 'inline-flex', color: 'var(--text-3)', cursor: 'help' }}
            >
              <IconInfo />
            </span>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <div style={{ minWidth: 150, flexShrink: 0 }}>
              <FormField label="Destination Code">
                <DestCodeCombobox
                  items={allLoose}
                  value={codeInput}
                  onChange={setCodeInput}
                  excludeCode={fromItem.code}
                  invalid={isSourceOwnCode}
                />
              </FormField>
            </div>
            {isNewDestCode && (
              <div style={{ minWidth: 120, flexShrink: 0 }}>
                <FormField label={`New SKU${fromItem.sku ? ' (opt.)' : ''}`}>
                  <input value={newSkuInput} onChange={e => setNewSkuInput(e.target.value)}
                    placeholder={fromItem.sku || 'e.g. MRG-0001'} style={{ fontFamily: 'var(--font-num)' }} />
                </FormField>
              </div>
            )}

            <div style={{ width: 90, flexShrink: 0 }}>
              <FormField label="Carat">
                <input className="no-spinner" type="number" step="0.01" min="0" max={remainingCarat}
                  value={caratInput} onChange={e => setCaratInput(e.target.value)}
                  onBlur={() => { if (num(caratInput) > remainingCarat + 1e-6) setCaratInput(remainingCarat.toFixed(2)); }}
                  placeholder={remainingCarat.toFixed(2)}
                  style={caratExceedsRemaining ? { borderColor: 'var(--danger)' } : undefined}
                />
              </FormField>
            </div>
            <div style={{ width: 90, flexShrink: 0 }}>
              <FormField label="Price/ct">
                <input className="no-spinner" type="number" step="0.01" min="0"
                  value={priceInput} onChange={e => setPriceInput(e.target.value)}
                  placeholder={money(remainingPricePerCt)}
                  style={cutValueExceedsRemaining ? { borderColor: 'var(--danger)' } : undefined} />
              </FormField>
            </div>
            {/* CalcField/FormField reuse the exact .form-group / .calc-field CSS
                the Carat/Price inputs use (same padding, border-width, font-size),
                so this box and button line up with them exactly instead of an
                approximated height guess. Add Cut's label is a non-breaking
                space — same .form-group label markup, just blank. */}
            <div style={{ width: 130, flexShrink: 0 }}>
              <FormField label="Total Amount">
                <CalcField value={money(num(caratInput) * num(priceInput))} />
              </FormField>
            </div>
            <div style={{ flexShrink: 0 }}>
              <FormField label=" ">
                <Button
                  variant="primary" onClick={addCut} disabled={isSourceOwnCode}
                  style={{ width: '100%', padding: '9px 16px', borderWidth: 1.5, lineHeight: 1.5, boxSizing: 'border-box' }}
                >
                  Add Cut
                </Button>
              </FormField>
            </div>
          </div>
          {caratExceedsRemaining && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--danger)' }}>
              Only {remainingCarat.toFixed(2)}ct remains on the source parcel.
            </div>
          )}
          {!caratExceedsRemaining && cutValueExceedsRemaining && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--danger)' }}>
              This cut would be worth {money(num(caratInput) * num(priceInput))}, but only {money(remainingValue)} of value
              remains on the source parcel — lower the Price/ct or carat.
            </div>
          )}
          {isSourceOwnCode && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--danger)' }}>
              That's the source parcel's own Code — pick a different destination.
            </div>
          )}

          {cutError && <div className="msg msg-error" style={{ marginTop: 10 }}>{cutError}</div>}

          {codeInput.trim() && !isSourceOwnCode && (
            matchedDest ? (
              <div style={{
                marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '7px 12px', background: 'var(--primary-light)', borderRadius: 'var(--r-sm)', fontSize: 12,
              }}>
                <span className="badge" style={{ background: 'var(--primary)', color: '#fff', flexShrink: 0 }}>Existing</span>
                <MatchFact label="Shape" value={matchedDest.shape} />
                <MatchFact label="Color / Clarity" value={`${matchedDest.color}/${matchedDest.clarity}`} />
                <MatchFact label="Available" value={`${availableCarat(matchedDest).toFixed(2)}ct`} />
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700, color: 'var(--primary)' }}>
                  <IconArrowRight /> Will blend into it
                </span>
              </div>
            ) : (
              <div style={{
                marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', background: 'var(--success-light)', borderRadius: 'var(--r-sm)',
              }}>
                <span className="badge" style={{ background: 'var(--success)', color: '#fff', flexShrink: 0 }}>New</span>
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                  Doesn't exist yet — will be created, inheriting Shape/Color/Clarity/Size from the source parcel.
                </span>
              </div>
            )
          )}
        </div>
      )}

      {/* ── Cuts (pending — nothing written until Complete) ── */}
      {fromItem && cuts.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Cuts</div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th><th>SKU</th><th>Size / Sub</th><th>Carat</th><th>Price/ct</th>
                  <th>Total Value</th><th>Status</th><th>Source Remaining After</th><th />
                </tr>
              </thead>
              <tbody>
                {cutsWithRunning.map(({ cut, total, remainingCaratAfter, remainingValueAfter, existing }) => (
                  <tr key={cut._key}>
                    <td><CodePill code={cut.code} /></td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{existing?.sku || cut.newSku || fromItem.sku || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {existing ? `${existing.size || '—'} / ${existing.subSize || '—'}` : `${fromItem.size || '—'} / ${fromItem.subSize || '—'}`}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{num(cut.carat).toFixed(2)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(num(cut.pricePerCt))}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(total)}</td>
                    <td>
                      <span className="badge" style={existing
                        ? { background: 'var(--primary-light)', color: 'var(--primary)' }
                        : { background: 'var(--success-light)', color: 'var(--success)' }}>
                        {existing ? 'Existing' : 'New'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>
                      {remainingCaratAfter.toFixed(2)}ct / {money(remainingValueAfter)}
                    </td>
                    <td>
                      <button type="button" className="line-item-remove" onClick={() => removeCut(cut._key)} title="Remove this pending cut">
                        <IconX />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {cuts.length} cut{cuts.length !== 1 ? 's' : ''} · <span style={{ fontFamily: 'var(--font-num)' }}>{cutsTotalCarat.toFixed(2)}ct</span> allocated ·
              <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}> {money(cutsTotalValue)}</span>
            </span>
            <Button variant="primary" onClick={handleComplete} loading={saving} style={{ marginLeft: 'auto' }}>
              Save
            </Button>
          </div>
        </div>
      )}

      {/* ── History — in its own modal (opened from the header) ── */}
      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        size="lg"
        title="Split & Merge History"
        subtitle="Every completed split, most recent first"
      >
        <div style={{ marginBottom: 12, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' }}>
            <IconSearch />
          </span>
          <input
            value={historyQ}
            onChange={e => setHistoryQ(e.target.value)}
            placeholder="Filter by From / To Code…"
            style={{
              width: '100%', paddingLeft: 32, paddingRight: 10, height: 34, fontSize: 12.5,
              border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
              background: 'var(--surface-2)', color: 'var(--text-1)', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {historyLoading ? (
          <div style={{ padding: '32px 20px', fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>Loading…</div>
        ) : historyRows.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 20px' }}>
            <div className="empty-state-icon" style={{ display: 'flex', justifyContent: 'center' }}><IconEmptyDiamond /></div>
            <div className="empty-state-title">{historyQ ? 'No matches' : 'No splits yet'}</div>
            <div className="empty-state-sub">
              {historyQ ? 'Nothing matches that Code.' : 'Every completed Split & Merge shows up here, most recent first.'}
            </div>
          </div>
        ) : (
          <>
            <div className="table-wrap" style={{ maxHeight: 440, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Date</th><th>From</th><th>To</th><th>Carat</th><th>Price/ct</th><th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {histPg.pageRows.map(row => (
                    <tr key={row.id}>
                      <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{row.data.date}</td>
                      <td><CodePill code={row.codeId} /></td>
                      <td><CodePill code={parseDestCode(row.data.notes)} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{(row.data.carat ?? 0).toFixed(2)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(row.data.pricePerCt ?? 0)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(row.data.amountUSD ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paging page={histPg.page} totalPages={histPg.totalPages} total={histPg.total} onChange={histPg.setPage} noun="split" />
            {splitHistory.length >= HISTORY_CAP && !historyQ && (
              <p className="form-hint" style={{ marginTop: 8 }}>Showing the {HISTORY_CAP} most recent splits.</p>
            )}
          </>
        )}
      </Modal>
    </AppLayout>
  );
}
